
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { ThumbsUp, MessageSquare, Share2, Bookmark, ArrowUpRight } from "lucide-react";

interface EngagementMetricsProps {
  timeframe: "7d" | "30d" | "90d" | "1y";
}

// Sample data
const engagementBreakdownData = [
  { name: "Likes", value: 45, icon: ThumbsUp, color: "#2563EB" },
  { name: "Comments", value: 25, icon: MessageSquare, color: "#F97316" },
  { name: "Shares", value: 20, icon: Share2, color: "#10B981" },
  { name: "Bookmarks", value: 10, icon: Bookmark, color: "#8B5CF6" }
];

const engagementByTopicData = [
  { name: "Politics", engagement: 85 },
  { name: "Technology", engagement: 78 },
  { name: "Health", engagement: 65 },
  { name: "Business", engagement: 72 },
  { name: "Science", engagement: 68 },
  { name: "Culture", engagement: 57 },
  { name: "Sports", engagement: 49 },
];

const commentsByTimeData = [
  { hour: "12am", comments: 5 },
  { hour: "3am", comments: 2 },
  { hour: "6am", comments: 8 },
  { hour: "9am", comments: 25 },
  { hour: "12pm", comments: 42 },
  { hour: "3pm", comments: 35 },
  { hour: "6pm", comments: 48 },
  { hour: "9pm", comments: 32 },
];

// Sample insights
const engagementInsights = [
  "Articles on technology topics receive 30% more shares than average",
  "Reader comments peak between 6pm-8pm on weekdays",
  "Posts with embedded videos have 2.5x higher engagement rate",
  "Questions in headlines generate 45% more comments",
  "Articles with 5-7 minute read times receive the most bookmarks"
];

const EngagementMetrics: React.FC<EngagementMetricsProps> = ({ timeframe }) => {
  return (
    <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
      <Card className="dark:bg-gray-800 dark:text-gray-100">
        <CardHeader>
          <CardTitle>Engagement Breakdown</CardTitle>
          <CardDescription>How readers interact with your content</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center">
            <ChartContainer
              config={{
                engagement: { 
                  theme: { light: '#F97316', dark: '#F97316' } 
                }
              }}
              className="h-64"
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={engagementBreakdownData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    outerRadius={80}
                    fill="#F97316"
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {engagementBreakdownData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <ChartTooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <ChartTooltipContent payload={payload} />
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
            {engagementBreakdownData.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                  <Icon className="h-4 w-4" />
                  <span className="text-sm">{item.name}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="dark:bg-gray-800 dark:text-gray-100">
        <CardHeader>
          <CardTitle>Engagement by Topic</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer
            config={{
              engagement: { 
                theme: { light: '#F97316', dark: '#F97316' } 
              }
            }}
            className="h-64"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={engagementByTopicData} layout="vertical">
                <XAxis type="number" domain={[0, 100]} />
                <YAxis type="category" dataKey="name" width={80} />
                <Bar dataKey="engagement" fill="var(--color-engagement, #F97316)" radius={[0, 4, 4, 0]}>
                  {engagementByTopicData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.engagement > 70 ? '#F97316' : entry.engagement > 60 ? '#FBBF24' : '#9CA3AF'} 
                    />
                  ))}
                </Bar>
                <ChartTooltip
                  formatter={(value) => [`${value}%`, 'Engagement Rate']}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="rounded-lg border bg-background p-2 shadow-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="font-medium">{payload[0].name}</div>
                            <div className="font-mono text-right">{payload[0].value}%</div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>

      <Card className="dark:bg-gray-800 dark:text-gray-100">
        <CardHeader>
          <CardTitle>Comments by Time of Day</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer
            config={{
              comments: { 
                theme: { light: '#2563EB', dark: '#2563EB' } 
              }
            }}
            className="h-64"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={commentsByTimeData}>
                <XAxis dataKey="hour" />
                <YAxis />
                <Bar dataKey="comments" fill="var(--color-comments, #2563EB)" radius={[4, 4, 0, 0]} />
                <ChartTooltip
                  formatter={(value) => [value, 'Comments']}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="rounded-lg border bg-background p-2 shadow-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="font-medium">{label}</div>
                            <div className="font-mono text-right">{payload[0].value}</div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>

      <Card className="dark:bg-gray-800 dark:text-gray-100">
        <CardHeader>
          <CardTitle>Key Engagement Insights</CardTitle>
          <CardDescription>Actionable intelligence to boost reader interaction</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            {engagementInsights.map((insight, index) => (
              <li key={index} className="flex items-start gap-2">
                <ArrowUpRight className="h-5 w-5 text-directpress-orange flex-shrink-0 mt-0.5" />
                <span>{insight}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default EngagementMetrics;
